import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'flux/utils';
import { browserHistory } from 'react-router';
import { SearchTypes } from '../../constants/CentricConstants';

import ListView from '../Common/ListView.react';
import AssetListItem from './AssetListItem';
import FlatButton from 'material-ui/FlatButton';
import LinearProgress from 'material-ui/LinearProgress';

import CancelIcon from 'material-ui/svg-icons/navigation/cancel';
import SearchIcon from 'material-ui/svg-icons/action/search';
import UpIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-up';
import DownIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-down';

import AssetActions from '../../actions/AssetActionCreator';
import AssetStore, { sortOptions } from '../../stores/AssetStore';
import AssetTypeStore from '../../stores/AssetTypeStore';

import buildContextMenu from '../Common/BuildContextMenu';
import AssetEditDialog from './AssetEditDialog';
import { capitalise } from '../../utils/StringUtils';

const getMenuItems = () => [
    {
        link: '/dashboard/assets/search',
        label: 'Search content',
        icon: <SearchIcon />
    },
    { type: 'divider' },
    {
        link: '/logout',
        icon: <CancelIcon />,
        label: 'Sign out',
    },
];

const getDefaultState = () => ({
    title: 'Assets',
    count: 0,
    page: 0,
    limit: 50,
    sort: 'name',
    showButton: false,
    searchPage: 0,
    searchMode: false,
    searchText: null,
    fetchedAll: false,
    clearFetched: false
});

class AssetList extends Component {

    componentDidMount() {
        this.sort = this.sort.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.search = this.search.bind(this);
        this.toggleSearchMode = this.toggleSearchMode.bind(this);
        this.filter = this.filter.bind(this);

        this.assetListener = AssetStore.addListener(() => {
            this.setState({fetchedAll: !this.state.clearFetched && AssetStore.fetchedAll(), clearFetched: false})
        });
        AssetActions.getAll({limit: this.state.limit, type: this.props.routeParams.type || null});
        AssetActions.getTypes();

        if (!!this.props.location.query.searchTerm) {
            this.search(this.props.location.query.searchTerm);
        }

        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        this.assetListener.remove();
        window.removeEventListener('scroll', this.handleScroll);
    }

    render() {
        return (
            <div ref="assetList">
                <ListView
                    title={this.state.title}
                    actions={buildContextMenu(getMenuItems())}
                    sortOptions={sortOptions}
                    sortAction={this.sort}
                    searchAction={this.search}
                    searchMode={this.state.searchMode || !!this.props.location.query.searchTerm}
                    searchType={SearchTypes.ASSETS}
                    filterOptions={[
                        {label: 'Show all', value: null},
                        ...this.state.types.map(({name}) => ({ label: capitalise(name), value: name }))
                    ]}
                    filterAction={this.filter}
                    items={this.state.assets}
                    itemDecorator={AssetListItem}
                    newEntityAction={this.context.auth.isAdmin() && AssetActions.create}
                    toggleSearchMode={this.toggleSearchMode}
                />
                {this.state.loading && (
                    <LinearProgress mode="indeterminate" />
                )}
                {this.state.showButton && (
                    <FlatButton
                        label={this.state.fetchedAll ? 'Back to top' : 'Load more'}
                        icon={this.state.fetchedAll ? <UpIcon/> : <DownIcon/>}
                        onTouchTap={this.state.fetchedAll ? () => window.scrollTo(0, 0) : this.loadNextPage}
                        disabled={this.state.loading}
                        fullWidth
                    />
                )}
                <AssetEditDialog />
            </div>
        );
    }

    sort({sort}) {
        this.setState({ sort });
        AssetActions.getAll({ sort });
    }

    filter({filter}) {
        this.setState({title: capitalise(filter) || 'Assets', isLoading: true, showButton: false});
        AssetActions.getAll({sort: this.state.sort || null, type: filter}, true);
        browserHistory.replace(!filter ? '/dashboard/assets' : `/dashboard/assets/${filter}`);
    }

    loadNextPage() {
        if (this.state.loading) { return; } //guard

        const page = !this.state.searchMode ? (this.state.page + 1) : (this.state.searchPage + 1);
        if (!this.state.searchMode) {
            AssetActions.getAll({page, limit: this.state.limit, type: this.props.routeParams.type || null});
            this.setState({page, loading: true});
        } else if (!!this.state.searchText) {
            AssetActions.search(this.state.searchText, {page, limit: this.state.limit});
            this.setState({searchPage: page, loading: true});
        }
    }

    handleScroll() {
        if (!this.state.showButton && window.scrollY > 0) {
            this.setState({showButton: true});
        }
        if (!this.state.fetchedAll && (window.scrollY + window.innerHeight) >= this.refs.assetList.clientHeight) {
            this.loadNextPage();
        }
    }

    search(searchText) {
        this.timeout && clearTimeout(this.timeout);
        if (!!searchText && searchText.length > 2) {
            this.timeout = setTimeout(() => { AssetActions.search(searchText, {limit: this.state.limit}); }, 500);

            if (searchText !== this.state.searchText) {
                this.setState({fetchedAll: false, showButton: false});
            }
            this.setState({searchText, searchPage: 0});
            browserHistory.replace(`${this.props.location.pathname}?searchTerm=${searchText}`);
        }
    }

    toggleSearchMode(searchMode) {
        if (!searchMode) {
            this.setState({searchText: null});
            browserHistory.replace(this.props.location.pathname);
        }
        this.setState({searchMode, fetchedAll: false, showButton: false, clearFetched: true});
    }
}

AssetList.contextTypes = {
    auth: PropTypes.object.isRequired
};

AssetList.getStores = () => ([AssetStore, AssetTypeStore]);
AssetList.calculateState = (prevState) => {
    const prev = (prevState || getDefaultState());
    return ({
        ...prev,
        assets: AssetStore.listAll(prev),
        loading: AssetStore.isFetching(),
        types: AssetTypeStore.getAll()
    });
};

export default Container.create(AssetList);
